import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { ArrowLeft, Package, RefreshCw, ChevronLeft, ChevronRight, Image } from 'lucide-react'
import { Skeleton } from '@/components/SkeletonLoader'
import { AlertModal, AlertType } from '@/components/AlertModal'
import { useState, useEffect } from 'react'

export default function FeedProductsPage() {
  const { feedId } = useParams<{ feedId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const queryClient = useQueryClient()
  
  // Pagination state
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    type: AlertType
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    onConfirm?: () => void
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  })

  // Fetch feed metadata
  const { data: feedData, isLoading: isLoadingFeed } = useQuery({
    queryKey: ['inventory-feed', feedId],
    queryFn: () => api.inventory.getFeed(feedId),
    enabled: !!feedId,
  })

  const feed = feedData?.data?.data

  // Fetch paginated products
  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['inventory-products', feedId, page, limit, debouncedSearch],
    queryFn: () => api.inventory.listProducts(feedId || '', {
      page,
      limit,
      search: debouncedSearch || undefined,
    }),
    enabled: !!feedId,
  })

  const products = productsData?.data?.data?.items || []
  const pagination = productsData?.data?.data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 }

  // Sync experiences mutation
  const syncExperiencesMutation = useMutation({
    mutationFn: async () => {
      if (!feed?.feedId) {
        throw new Error('No feed found')
      }
      posthog.capture('experiences_sync_started', { feed_id: feedId })
      return api.inventory.syncExperiences(feed.feedId.toString(), { vendorId: vendorId ? parseInt(vendorId.toString()) : undefined })
    },
    onSuccess: (response) => {
      posthog.capture('experiences_sync_completed', { feed_id: feedId })
      queryClient.invalidateQueries({ queryKey: ['inventory-feed', feedId] })
      queryClient.invalidateQueries({ queryKey: ['inventory-products', feedId] })
      const data = response.data?.data || response.data
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: data?.message || `Successfully synced experiences from ${feed?.platform}`,
        confirmText: 'OK',
      })
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Failed to sync experiences'
      posthog.capture('experiences_sync_failed', { feed_id: feedId, error: msg })
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: msg,
        confirmText: 'OK',
      })
    },
  })

  // Strip HTML tags helper
  const stripHtml = (html: string) => {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, '').substring(0, 100)
  }

  if (isLoadingFeed) {
    return (
      <div className="container">
        <div style={{ marginBottom: '2rem' }}>
          <Skeleton height="2rem" width="300px" style={{ marginBottom: '0.5rem', borderRadius: '4px' }} />
          <Skeleton height="1rem" width="400px" style={{ borderRadius: '4px' }} />
        </div>
        <Skeleton height="400px" style={{ borderRadius: '8px' }} />
      </div>
    )
  }

  if (!feed) {
    return (
      <div className="container">
        <a
          href="/vendor/inventory"
          onClick={(e) => { e.preventDefault(); navigate('/vendor/inventory') }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-sky-blue)', textDecoration: 'none', marginBottom: '1rem' }}
        >
          <ArrowLeft size={16} />
          Back to Inventory
        </a>
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <Package size={48} style={{ margin: '0 auto 1rem', color: 'var(--color-gray-400)' }} />
          <h3 style={{ marginBottom: '0.5rem' }}>Feed Not Found</h3>
          <p style={{ color: 'var(--color-gray-600)' }}>
            The requested feed could not be found.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <a
          href="/vendor/inventory"
          onClick={(e) => { e.preventDefault(); navigate('/vendor/inventory') }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-sky-blue)', textDecoration: 'none', marginBottom: '1rem', fontSize: '0.875rem' }}
        >
          <ArrowLeft size={16} />
          Back to Inventory
        </a>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ marginBottom: '0.5rem' }}>
              Experiences - {feed?.merchant?.name || 'Feed Experiences'}
            </h2>
            <p style={{ color: 'var(--color-gray-600)', fontSize: '0.9rem' }}>
              {pagination.total} {pagination.total === 1 ? 'experience' : 'experiences'} in this feed
              {feed?.platform && feed.platform !== 'tixxly' && (
                <span style={{ marginLeft: '0.5rem', textTransform: 'capitalize' }}>• {feed.platform}</span>
              )}
            </p>
          </div>
          {feed?.platform && feed.platform !== 'tixxly' && (
            <button
              className="button"
              onClick={() => syncExperiencesMutation.mutate()}
              disabled={syncExperiencesMutation.isPending}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                border: '1px solid var(--color-gray-300)',
                backgroundColor: 'white',
                color: 'var(--color-gray-700)',
                opacity: syncExperiencesMutation.isPending ? 0.6 : 1,
                cursor: syncExperiencesMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              <RefreshCw size={16} style={{ animation: syncExperiencesMutation.isPending ? 'spin 1s linear infinite' : 'none' }} />
              {syncExperiencesMutation.isPending ? 'Syncing...' : 'Sync Experiences'}
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1.5rem' }}>
        <input
          type="text"
          placeholder="Search experiences..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input"
          style={{ maxWidth: '400px', width: '100%' }}
        />
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid var(--color-gray-200)',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}>
        {isLoadingProducts ? (
          <div style={{ padding: '2rem' }}>
            <Skeleton height="40px" style={{ marginBottom: '0.5rem' }} />
            <Skeleton height="60px" style={{ marginBottom: '0.5rem' }} />
            <Skeleton height="60px" style={{ marginBottom: '0.5rem' }} />
            <Skeleton height="60px" />
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Package size={48} style={{ margin: '0 auto 1rem', color: 'var(--color-gray-400)' }} />
            <h3 style={{ marginBottom: '0.5rem' }}>No Experiences</h3>
            <p style={{ color: 'var(--color-gray-600)', marginBottom: '1rem' }}>
              {debouncedSearch ? 'No experiences match your search' : 'This feed has no experiences yet.'}
            </p>
            {!debouncedSearch && feed?.platform && feed.platform !== 'tixxly' && (
              <>
                <div style={{ maxWidth: '480px', margin: '0 auto 1.5rem', padding: '1rem', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fbbf24', textAlign: 'left' }}>
                  <p style={{ fontSize: 'var(--text-sm)', color: '#92400e', margin: '0 0 0.5rem', fontWeight: 600 }}>No products found from your provider?</p>
                  <p style={{ fontSize: 'var(--text-xs)', color: '#92400e', margin: 0, lineHeight: 1.6 }}>
                    Verify that your products are fully set up, live, and included in your contracts with your provider. Most issues are on the provider setup or contract scope rather than on our side.
                  </p>
                </div>
                <button
                  className="button button-primary"
                  onClick={() => syncExperiencesMutation.mutate()}
                  disabled={syncExperiencesMutation.isPending}
                >
                  <RefreshCw size={16} style={{ marginRight: '0.5rem' }} />
                  Sync from {feed.platform}
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>
                      Image
                    </th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>
                      Experience
                    </th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>
                      Status
                    </th>
                    <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product: any, index: number) => {
                    const imageUrl = product.media?.[0]?.url || product.content?.media?.[0]?.url
                    const price = product.offers?.[0]?.price
                    const currency = product.offers?.[0]?.currency || 'USD'
                    
                    return (
                      <tr 
                        key={product.product_id || `product-${index}`}
                        style={{ 
                          borderBottom: index < products.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/vendor/inventory/experiences/edit/${product.product_id}`, { state: { product } })}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-gray-50)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            backgroundColor: 'var(--color-gray-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={product.content?.title || 'Experience'}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none'
                                }}
                              />
                            ) : (
                              <Image size={20} style={{ color: 'var(--color-gray-400)' }} />
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', maxWidth: '400px' }}>
                          <div style={{ fontWeight: 500, color: 'var(--color-gray-900)', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {product.content?.title || product.product_id_external || 'Untitled'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {stripHtml(product.content?.description) || 'No description'}
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{
                            display: 'inline-flex',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textTransform: 'capitalize',
                            backgroundColor:
                              product.status === 'active' ? '#dcfce7' :
                              product.status === 'inactive' ? '#fef3c7' : '#e5e7eb',
                            color:
                              product.status === 'active' ? '#166534' :
                              product.status === 'inactive' ? '#92400e' : '#374151',
                          }}>
                            {product.status}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          {price !== undefined ? (
                            <div style={{ fontWeight: 600, color: 'var(--color-gray-900)' }}>
                              {currency} {price.toFixed(2)}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--color-gray-400)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                borderTop: '1px solid var(--color-gray-200)',
                backgroundColor: 'var(--color-gray-50)',
                flexWrap: 'wrap',
                gap: '1rem',
              }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, pagination.total)} of {pagination.total}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      opacity: page === 1 ? 0.5 : 1,
                    }}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)', padding: '0 0.5rem' }}>
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      cursor: page === pagination.totalPages ? 'not-allowed' : 'pointer',
                      opacity: page === pagination.totalPages ? 0.5 : 1,
                    }}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={alertModal.onConfirm}
        onClose={() => setAlertModal({ isOpen: false, type: 'info', title: '', message: '' })}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />
    </div>
  )
}
